import { Trash2, Type } from 'lucide-react'
import { useFonts } from '@/api/endpoints/fonts'
import { fmt } from '../format'
import {
  MAX_TRANSITION_S,
  SHADOW_SIZES,
  TRANSITION_OPTIONS,
  type TextOverlay,
  type TextTransition,
  type TransitionKind,
} from '../model'
import { InspectorPanel } from './InspectorPanel'
import styles from '../styles/inspector.module.scss'

export interface TextInspectorProps {
  text: TextOverlay
  onPatch: (patch: Partial<TextOverlay>) => void
  onDelete: () => void
}

/** Font size presets, as a fraction of frame height. Absolute pixels would
 * mean nothing across a 720p proxy and a 4K burn-in. */
const SIZES = [
  { label: 'P', value: 0.04 },
  { label: 'M', value: 0.06 },
  { label: 'G', value: 0.09 },
  { label: 'GG', value: 0.13 },
] as const

const BACKGROUNDS = [
  { label: 'Nenhum', value: 'none' },
  { label: 'Sombra', value: 'shadow' },
  { label: 'Caixa', value: 'box' },
] as const

/** Everything about the selected caption except its position and content,
 * which are edited directly on the video. */
export function TextInspector({ text, onPatch, onDelete }: TextInspectorProps) {
  const fonts = useFonts()

  return (
    <InspectorPanel
      icon={<Type aria-hidden />}
      title="Texto selecionado"
      meta={`${fmt(text.start)} – ${fmt(text.end)}`}
      actions={
        <button
          type="button"
          className={styles.inspectorDelete}
          onClick={onDelete}
          aria-label="Excluir texto"
          title="Excluir texto"
        >
          <Trash2 aria-hidden />
        </button>
      }
    >

      <label className={styles.field}>
        <span className={styles.fieldLabel}>Fonte</span>
        <select
          className={styles.select}
          value={text.fontFamily}
          onChange={(e) => {
            onPatch({ fontFamily: e.target.value })
          }}
        >
          {/* Rendered in the family itself: picking a display face is a
              visual decision, and a list in one typeface hides it. */}
          {(fonts.data ?? []).map((font) => (
            <option key={font.family} value={font.family} style={{ fontFamily: font.family }}>
              {font.family}
            </option>
          ))}
          {/* A family saved before the file was removed still shows, so the
              caption doesn't silently switch typeface behind the user. */}
          {(fonts.data ?? []).some((f) => f.family === text.fontFamily) ? null : (
            <option value={text.fontFamily}>{text.fontFamily} (indisponível)</option>
          )}
        </select>
      </label>

      <div className={styles.field}>
        <span className={styles.fieldLabel}>Tamanho</span>
        <div className={styles.chips}>
          {SIZES.map((size) => (
            <button
              key={size.label}
              type="button"
              className={styles.chip}
              aria-pressed={Math.abs(text.size - size.value) < 0.005}
              onClick={() => {
                onPatch({ size: size.value })
              }}
            >
              {size.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.field}>
        <span className={styles.fieldLabel}>Fundo</span>
        <div className={styles.chips}>
          {BACKGROUNDS.map((bg) => (
            <button
              key={bg.value}
              type="button"
              className={styles.chip}
              aria-pressed={text.background === bg.value}
              onClick={() => {
                onPatch({ background: bg.value })
              }}
            >
              {bg.label}
            </button>
          ))}
        </div>
      </div>

      {text.background === 'shadow' ? (
        <>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Sombra</span>
            <div className={styles.chips}>
              {SHADOW_SIZES.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  className={styles.chip}
                  aria-pressed={Math.abs(text.shadowSize - preset.value) < 0.005}
                  onClick={() => {
                    onPatch({ shadowSize: preset.value })
                  }}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Cor da sombra</span>
            <input
              type="color"
              className={styles.color}
              value={text.shadowColor}
              onChange={(e) => {
                onPatch({ shadowColor: e.target.value })
              }}
              aria-label="Cor da sombra"
            />
          </label>
        </>
      ) : null}

      <label className={styles.field}>
        <span className={styles.fieldLabel}>Cor</span>
        <input
          type="color"
          className={styles.color}
          value={text.color}
          onChange={(e) => {
            onPatch({ color: e.target.value })
          }}
          aria-label="Cor do texto"
        />
      </label>

      <TransitionField
        label="Entrada"
        value={text.transitionIn}
        span={text.end - text.start}
        onChange={(transitionIn) => {
          onPatch({ transitionIn })
        }}
      />
      <TransitionField
        label="Saída"
        value={text.transitionOut}
        span={text.end - text.start}
        onChange={(transitionOut) => {
          onPatch({ transitionOut })
        }}
      />

      <p className={styles.hintSmall}>
        Arraste o texto sobre o vídeo para posicionar · dois cliques para editar
      </p>
    </InspectorPanel>
  )
}

interface TransitionFieldProps {
  label: string
  value: TextTransition
  /** The caption's own length — the duration slider can't be allowed past
   * half of it, or the entrance and the exit would overlap and the caption
   * would never be fully on screen. */
  span: number
  onChange: (value: TextTransition) => void
}

function TransitionField({ label, value, span, onChange }: TransitionFieldProps) {
  const max = Math.max(0.1, Math.min(MAX_TRANSITION_S, span / 2))
  const clamped = Math.min(value.duration, max)

  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <select
        className={styles.select}
        value={value.kind}
        onChange={(e) => {
          onChange({ ...value, kind: e.target.value as TransitionKind })
        }}
        aria-label={`Transição de ${label.toLowerCase()}`}
      >
        {TRANSITION_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {value.kind === 'none' ? null : (
        <div className={styles.sliderRow}>
          <input
            type="range"
            className={styles.slider}
            min={0.1}
            max={max}
            step={0.05}
            value={clamped}
            onChange={(e) => {
              onChange({ ...value, duration: Number(e.target.value) })
            }}
            aria-label={`Duração da ${label.toLowerCase()}`}
          />
          <span className={styles.sliderValue}>{clamped.toFixed(2).replace('.', ',')}s</span>
        </div>
      )}
    </div>
  )
}
